import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Send, RefreshCw, Search, FileText, Edit2, X, CheckCircle2, XCircle, AlertCircle, Megaphone, Users, Plus, Eye, Trash2, Zap } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { VisualTemplateEditor } from "@/components/EmailVisualEditor";
import { AutomationsPanel } from "@/components/AdminAutomations";
import { AutomationRunsPanel } from "@/components/AdminAutomationRuns";
import { StarterPackButton } from "@/components/AdminStarterPack";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRAND_PILL = {
  collective: { label: "Collective", bg: "#0D2F4D", fg: "#C6D21F" },
  careers:    { label: "Careers",    bg: "#C6D21F", fg: "#0D2F4D" },
};

function BrandBadge({ brand }) {
  const b = BRAND_PILL[brand] || { label: brand, bg: "#6F8FA8", fg: "#fff" };
  return (
    <span
      className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wide uppercase"
      style={{ background: b.bg, color: b.fg }}
      data-testid={`email-hub-brand-${brand}`}
    >
      {b.label}
    </span>
  );
}

const ENTRY_TYPE_STYLE = {
  website: "border-sky-200 bg-sky-50 text-sky-800",
  import: "border-amber-200 bg-amber-50 text-amber-800",
  manual: "border-emerald-200 bg-emerald-50 text-emerald-800",
};

function EntryTypeBadge({ type, id }) {
  const value = ["website", "import", "manual"].includes(type) ? type : "website";
  return (
    <Badge
      variant="outline"
      className={ENTRY_TYPE_STYLE[value]}
      data-testid={`subscriber-entry-type-${id}`}
    >
      {value.charAt(0).toUpperCase() + value.slice(1)}
    </Badge>
  );
}

function StatusBadge({ status }) {
  if (status === "sent")     return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100"><CheckCircle2 className="h-3 w-3 mr-1"/>sent</Badge>;
  if (status === "failed")   return <Badge className="bg-red-100 text-red-800 hover:bg-red-100"><XCircle className="h-3 w-3 mr-1"/>failed</Badge>;
  if (status === "skipped")  return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100"><AlertCircle className="h-3 w-3 mr-1"/>skipped</Badge>;
  return <Badge variant="secondary">{status}</Badge>;
}

export function EmailHubTab({ pw }) {
  const authHeaders = useMemo(() => ({ "X-Admin-Password": pw }), [pw]);
  const [sub, setSub] = useState("templates");
  const [reloadKey, setReloadKey] = useState(0);

  return (
    <div className="space-y-6" data-testid="admin-email-hub">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-[#0D2F4D] flex items-center gap-2">
            <Mail className="h-6 w-6"/>Email Hub
          </h2>
          <p className="text-sm text-[#6F8FA8] mt-1 max-w-2xl">
            Central inbox for every outbound email across The Ditch Collective and The Ditch Careers.
            Edit template copy, watch the log, and send yourself a test — no third-party dashboard needed.
          </p>
        </div>
        <StarterPackButton authHeaders={authHeaders} onInstalled={() => setReloadKey(k => k + 1)} />
      </div>

      <Tabs value={sub} onValueChange={setSub}>
        <TabsList className="bg-[#F2F2EF] p-1.5 rounded-2xl h-auto flex-wrap">
          <TabsTrigger
            value="stats"
            data-testid="email-hub-tab-stats"
            className="data-[state=active]:bg-[#0D2F4D] data-[state=active]:text-[#C6D21F] text-[#0D2F4D] font-semibold text-xs px-4 py-2 rounded-xl"
          >Stats</TabsTrigger>
          <TabsTrigger
            value="segments"
            data-testid="email-hub-tab-segments"
            className="data-[state=active]:bg-[#0D2F4D] data-[state=active]:text-[#C6D21F] text-[#0D2F4D] font-semibold text-xs px-4 py-2 rounded-xl"
          >Segments</TabsTrigger>
          <TabsTrigger
            value="templates"
            data-testid="email-hub-tab-templates"
            className="data-[state=active]:bg-[#0D2F4D] data-[state=active]:text-[#C6D21F] text-[#0D2F4D] font-semibold text-xs px-4 py-2 rounded-xl"
          >Templates</TabsTrigger>
          <TabsTrigger
            value="automations"
            data-testid="email-hub-tab-automations"
            className="data-[state=active]:bg-[#0D2F4D] data-[state=active]:text-[#C6D21F] text-[#0D2F4D] font-semibold text-xs px-4 py-2 rounded-xl"
          >Automations</TabsTrigger>
          <TabsTrigger
            value="runs"
            data-testid="email-hub-tab-runs"
            className="data-[state=active]:bg-[#0D2F4D] data-[state=active]:text-[#C6D21F] text-[#0D2F4D] font-semibold text-xs px-4 py-2 rounded-xl"
          >Runs</TabsTrigger>
          <TabsTrigger
            value="campaigns"
            data-testid="email-hub-tab-campaigns"
            className="data-[state=active]:bg-[#0D2F4D] data-[state=active]:text-[#C6D21F] text-[#0D2F4D] font-semibold text-xs px-4 py-2 rounded-xl"
          >Campaigns</TabsTrigger>
          <TabsTrigger
            value="subscribers"
            data-testid="email-hub-tab-subscribers"
            className="data-[state=active]:bg-[#0D2F4D] data-[state=active]:text-[#C6D21F] text-[#0D2F4D] font-semibold text-xs px-4 py-2 rounded-xl"
          >Marketing list</TabsTrigger>
          <TabsTrigger
            value="logs"
            data-testid="email-hub-tab-logs"
            className="data-[state=active]:bg-[#0D2F4D] data-[state=active]:text-[#C6D21F] text-[#0D2F4D] font-semibold text-xs px-4 py-2 rounded-xl"
          >Send log</TabsTrigger>
          <TabsTrigger
            value="overview"
            data-testid="email-hub-tab-overview"
            className="data-[state=active]:bg-[#0D2F4D] data-[state=active]:text-[#C6D21F] text-[#0D2F4D] font-semibold text-xs px-4 py-2 rounded-xl"
          >Overview</TabsTrigger>
        </TabsList>

        <TabsContent value="stats"     className="mt-6"><Stats authHeaders={authHeaders}/></TabsContent>
        <TabsContent value="segments"  className="mt-6"><Segments authHeaders={authHeaders}/></TabsContent>
        <TabsContent value="overview"  className="mt-6"><Overview authHeaders={authHeaders}/></TabsContent>
        <TabsContent value="templates" className="mt-6"><Templates key={`tpl-${reloadKey}`} authHeaders={authHeaders}/></TabsContent>
        <TabsContent value="automations" className="mt-6"><AutomationsPanel key={`auto-${reloadKey}`} authHeaders={authHeaders}/></TabsContent>
        <TabsContent value="runs" className="mt-6"><AutomationRunsPanel authHeaders={authHeaders}/></TabsContent>
        <TabsContent value="campaigns" className="mt-6"><Campaigns authHeaders={authHeaders}/></TabsContent>
        <TabsContent value="subscribers" className="mt-6"><Subscribers authHeaders={authHeaders}/></TabsContent>
        <TabsContent value="logs"      className="mt-6"><Logs authHeaders={authHeaders}/></TabsContent>
      </Tabs>
    </div>
  );
}

/* ── Overview ─────────────────────────────────────────────────────────── */
function Overview({ authHeaders }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/email-hub/summary`, { headers: authHeaders })
      .then(r => setData(r.data))
      .catch(e => toast.error(`Summary failed: ${e?.response?.data?.detail || e.message}`));
  }, [authHeaders]);
  if (!data) return <div className="text-sm text-[#6F8FA8]">Loading…</div>;

  return (
    <div className="grid gap-4 md:grid-cols-2" data-testid="email-hub-overview">
      <Card>
        <CardHeader><CardTitle className="text-base">Templates</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-[#6F8FA8]">Total</span>
            <span className="text-2xl font-bold text-[#0D2F4D]" data-testid="email-hub-templates-total">{data.templates.total}</span>
          </div>
          <div className="flex items-center justify-between">
            <BrandBadge brand="collective"/>
            <span className="text-lg font-semibold">{data.templates.by_brand.collective || 0}</span>
          </div>
          <div className="flex items-center justify-between">
            <BrandBadge brand="careers"/>
            <span className="text-lg font-semibold">{data.templates.by_brand.careers || 0}</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Sends (last 7 days)</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-emerald-700 font-semibold">Sent</span>
            <span className="text-2xl font-bold">{data.sends_last_7d.sent}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-red-700 font-semibold">Failed</span>
            <span className="text-lg font-semibold">{data.sends_last_7d.failed}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-amber-700 font-semibold">Skipped</span>
            <span className="text-lg font-semibold">{data.sends_last_7d.skipped}</span>
          </div>
        </CardContent>
      </Card>

      <Card className="md:col-span-2">
        <CardHeader><CardTitle className="text-base">Brand identities</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-4">
          {Object.entries(data.brands).map(([b, cfg]) => (
            <div key={b} className="border border-[#e2dccd] rounded-xl p-4 bg-[#FCFBF8]">
              <div className="flex items-center gap-2 mb-2"><BrandBadge brand={b}/></div>
              <div className="text-xs text-[#6F8FA8]">From name</div>
              <div className="text-sm font-semibold text-[#0D2F4D] mb-2">{cfg.from_name}</div>
              <div className="text-xs text-[#6F8FA8]">Reply-to</div>
              <div className="text-sm text-[#0D2F4D] mb-2">{cfg.reply_to}</div>
              <div className="text-xs text-[#6F8FA8]">Team inbox</div>
              <div className="text-sm text-[#0D2F4D]">{cfg.team_email}</div>
            </div>
          ))}
        </CardContent>
      </Card>

      {data.top_templates_7d?.length > 0 && (
        <Card className="md:col-span-2">
          <CardHeader><CardTitle className="text-base">Top templates (last 7 days)</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {data.top_templates_7d.map(r => (
              <div key={r.template_key} className="flex items-center justify-between text-sm border-b border-[#e2dccd] pb-2 last:border-0">
                <code className="text-[#0D2F4D]">{r.template_key}</code>
                <span className="font-semibold">{r.count}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ── Templates list + editor ─────────────────────────────────────────── */
function Templates({ authHeaders }) {
  const [rows, setRows] = useState([]);
  const [brand, setBrand] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [newBrand, setNewBrand] = useState("collective");
  const [newSubject, setNewSubject] = useState("");
  const [checked, setChecked] = useState(() => new Set());

  const load = useCallback(() => {
    axios.get(`${API}/admin/email-hub/templates`, { headers: authHeaders })
      .then(r => setRows(r.data.templates || []))
      .catch(e => toast.error(`Templates load failed: ${e?.response?.data?.detail || e.message}`));
  }, [authHeaders]);
  useEffect(() => { load(); }, [load]);

  const createTemplate = async () => {
    const slug = newKey.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_");
    if (!slug) return toast.error("Give the template a key (e.g. welcome_email).");
    if (!newSubject.trim()) return toast.error("Subject required.");
    try {
      await axios.post(`${API}/admin/email-hub/templates`, {
        key: slug,
        brand: newBrand,
        subject: newSubject.trim(),
        preview_text: "",
        headline: "",
        description: "Created from the visual editor.",
        blocks: [
          { type: "logo",   props: { src: "auto", align: "center", width: 96 } },
          { type: "header", props: { text: newSubject.trim(), align: "center", size: "h1" } },
          { type: "text",   props: { content: "Hi {{first_name}},\n\nWrite your message here." } },
          { type: "button", props: { label: "Learn more", href: "https://theditchcollective.co.nz", align: "center" } },
        ],
      }, { headers: authHeaders });
      toast.success(`Created ${slug}.`);
      setCreating(false); setNewKey(""); setNewSubject("");
      load();
      setSelected(slug);
    } catch (e) {
      toast.error(`Create failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const quickSendTest = async (key) => {
    const to = window.prompt("Send test to which email address?", "hello@theditchcollective.co.nz");
    if (!to) return;
    try {
      const r = await axios.post(
        `${API}/admin/email-hub/test-send`,
        { template_key: key, to_email: to, context: {} },
        { headers: authHeaders },
      );
      if (r.data.ok) toast.success(`Test sent → ${to}`);
      else toast.error(`Send failed: ${r.data.error || "unknown"}`);
    } catch (e) {
      toast.error(`Send failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const deleteTemplate = async (key) => {
    if (!window.confirm(`Delete template "${key}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/email-hub/templates/${key}`, { headers: authHeaders });
      toast.success(`Deleted ${key}.`);
      setChecked((s) => { const n = new Set(s); n.delete(key); return n; });
      load();
    } catch (e) {
      toast.error(`Delete failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const toggleOne = (key) => {
    setChecked((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  };

  const bulkDelete = async () => {
    const keys = Array.from(checked);
    if (keys.length === 0) return;
    if (!window.confirm(`Delete ${keys.length} template${keys.length === 1 ? "" : "s"}? Seed templates will be skipped automatically.`)) return;
    try {
      const r = await axios.post(
        `${API}/admin/email-hub/templates/bulk-delete`,
        { keys },
        { headers: authHeaders },
      );
      const { deleted = [], skipped_seed = [], not_found = [] } = r.data || {};
      if (deleted.length) toast.success(`Deleted ${deleted.length} template${deleted.length === 1 ? "" : "s"}.`);
      if (skipped_seed.length) toast(`${skipped_seed.length} seed template${skipped_seed.length === 1 ? "" : "s"} skipped (can't delete).`);
      if (not_found.length) toast.error(`${not_found.length} template${not_found.length === 1 ? "" : "s"} not found.`);
      setChecked(new Set());
      load();
    } catch (e) {
      toast.error(`Bulk delete failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const filtered = rows.filter(r => {
    if (brand !== "all" && r.brand !== brand) return false;
    if (q && !(r.key.toLowerCase().includes(q.toLowerCase())
              || (r.subject||"").toLowerCase().includes(q.toLowerCase())
              || (r.description||"").toLowerCase().includes(q.toLowerCase())
              || (r.trigger||"").toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-center flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6F8FA8]"/>
          <Input
            placeholder="Search template key, subject, trigger…"
            className="pl-9"
            value={q}
            onChange={e => setQ(e.target.value)}
            data-testid="email-hub-templates-search"
          />
        </div>
        <Select value={brand} onValueChange={setBrand}>
          <SelectTrigger className="w-[180px]" data-testid="email-hub-templates-brand-filter">
            <SelectValue placeholder="All brands"/>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All brands</SelectItem>
            <SelectItem value="collective">Collective</SelectItem>
            <SelectItem value="careers">Careers</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={load} data-testid="email-hub-templates-refresh">
          <RefreshCw className="h-4 w-4 mr-1"/>Refresh
        </Button>
        <Button
          size="sm"
          onClick={() => setCreating(true)}
          data-testid="email-hub-template-new"
          className="bg-[#0D2F4D] hover:bg-[#0D2F4D]/90 text-[#C6D21F]"
        >
          <Plus className="h-4 w-4 mr-1"/>New template
        </Button>
        {checked.size > 0 && (
          <Button
            size="sm"
            variant="outline"
            onClick={bulkDelete}
            data-testid="email-hub-templates-bulk-delete"
            className="border-red-500 text-red-600 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4 mr-1"/>Delete {checked.size} selected
          </Button>
        )}
      </div>

      {creating && (
        <Dialog open={true} onOpenChange={(o) => !o && setCreating(false)}>
          <DialogContent data-testid="email-hub-new-template-dialog">
            <DialogHeader><DialogTitle>Create a new email template</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold block mb-1">Key (URL-safe, unique)</label>
                <Input value={newKey} onChange={(e) => setNewKey(e.target.value)} placeholder="welcome_email" data-testid="email-hub-new-template-key"/>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold block mb-1">Brand</label>
                <Select value={newBrand} onValueChange={setNewBrand}>
                  <SelectTrigger data-testid="email-hub-new-template-brand"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="collective">The Ditch Collective</SelectItem>
                    <SelectItem value="careers">The Ditch Careers</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold block mb-1">Subject</label>
                <Input value={newSubject} onChange={(e) => setNewSubject(e.target.value)} placeholder="Welcome, {{first_name}}" data-testid="email-hub-new-template-subject"/>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
              <Button onClick={createTemplate} className="bg-[#0D2F4D] hover:bg-[#0D2F4D]/90 text-[#C6D21F]" data-testid="email-hub-new-template-create">Create + open editor</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <Card>
        <Table data-testid="email-hub-templates-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40px]">
                <input
                  type="checkbox"
                  checked={filtered.length > 0 && filtered.every(t => checked.has(t.key))}
                  onChange={(e) => {
                    setChecked((s) => {
                      const n = new Set(s);
                      if (e.target.checked) filtered.forEach(t => n.add(t.key));
                      else filtered.forEach(t => n.delete(t.key));
                      return n;
                    });
                  }}
                  data-testid="email-hub-templates-select-all"
                  className="h-4 w-4 rounded border-[#D6DCE3] cursor-pointer"
                />
              </TableHead>
              <TableHead className="w-[110px]">Brand</TableHead>
              <TableHead>Template</TableHead>
              <TableHead>Subject</TableHead>
              <TableHead>Trigger</TableHead>
              <TableHead className="w-[170px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(t => (
              <TableRow key={t.key} data-testid={`email-hub-template-row-${t.key}`}>
                <TableCell>
                  <input
                    type="checkbox"
                    checked={checked.has(t.key)}
                    onChange={() => toggleOne(t.key)}
                    data-testid={`email-hub-template-check-${t.key}`}
                    className="h-4 w-4 rounded border-[#D6DCE3] cursor-pointer"
                  />
                </TableCell>
                <TableCell><BrandBadge brand={t.brand}/></TableCell>
                <TableCell className="text-xs text-[#0D2F4D]">
                  <div className="font-semibold">{t.name || t.key}</div>
                  {t.name && <div className="font-mono text-[10px] text-[#6F8FA8]">{t.key}</div>}
                </TableCell>
                <TableCell className="text-sm max-w-[280px] truncate">{t.subject}</TableCell>
                <TableCell className="text-xs text-[#6F8FA8] max-w-[240px] truncate">{t.trigger}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button
                    size="sm" variant="outline"
                    onClick={() => quickSendTest(t.key)}
                    data-testid={`email-hub-quick-test-${t.key}`}
                    title="Send test to yourself"
                  ><Send className="h-3.5 w-3.5"/></Button>
                  <Button
                    variant="outline" size="sm"
                    onClick={() => setSelected(t.key)}
                    data-testid={`email-hub-template-edit-${t.key}`}
                  ><Edit2 className="h-3.5 w-3.5"/></Button>
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => deleteTemplate(t.key)}
                    data-testid={`email-hub-template-delete-${t.key}`}
                    title="Delete template"
                  ><Trash2 className="h-3.5 w-3.5 text-red-500"/></Button>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-sm text-[#6F8FA8] py-6">No templates match</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {selected && (
        <VisualTemplateEditor
          templateKey={selected}
          authHeaders={authHeaders}
          onClose={() => setSelected(null)}
          onSaved={() => { load(); }}
        />
      )}
    </div>
  );
}

function TemplateEditor({ templateKey, authHeaders, onClose, onSaved }) {
  const [tpl, setTpl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [dirty, setDirty] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewErr, setPreviewErr] = useState("");
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/email-hub/templates/${templateKey}`, { headers: authHeaders })
      .then(r => { setTpl(r.data); setDirty(false); })
      .catch(e => toast.error(`Load failed: ${e?.response?.data?.detail || e.message}`));
  }, [templateKey, authHeaders]);

  const field = (key, label, multiline = false, rows = 3, testid) => (
    <div className="space-y-1.5">
      <label className="text-xs font-semibold text-[#6F8FA8] uppercase tracking-wide">{label}</label>
      {multiline
        ? <Textarea
            value={tpl?.[key] || ""} rows={rows}
            onChange={e => { setTpl(prev => ({ ...prev, [key]: e.target.value })); setDirty(true); }}
            data-testid={testid}
            className="font-mono text-sm"
          />
        : <Input
            value={tpl?.[key] || ""}
            onChange={e => { setTpl(prev => ({ ...prev, [key]: e.target.value })); setDirty(true); }}
            data-testid={testid}
          />}
    </div>
  );

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/email-hub/templates/${templateKey}`, {
        subject: tpl.subject,
        preview_text: tpl.preview_text,
        headline: tpl.headline,
        body: tpl.body,
        description: tpl.description,
      }, { headers: authHeaders });
      toast.success("Template saved");
      setDirty(false);
      onSaved();
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const openPreview = async () => {
    if (!tpl) return;
    setPreviewOpen(true);
    setPreviewing(true);
    setPreviewErr("");
    setPreviewHtml("");
    try {
      const r = await axios.post(`${API}/admin/email-hub/preview`, {
        template_key: templateKey,
        subject: tpl.subject,
        preview_text: tpl.preview_text,
        headline: tpl.headline,
        body: tpl.body,
      }, { headers: authHeaders });
      setPreviewHtml(r.data.html || "");
    } catch (e) {
      setPreviewErr(e?.response?.data?.detail || e.message);
    } finally {
      setPreviewing(false);
    }
  };

  const sendTest = async () => {
    if (!testTo) return toast.error("Enter a test email address");
    try {
      const r = await axios.post(`${API}/admin/email-hub/test-send`,
        { template_key: templateKey, to_email: testTo, context: {} },
        { headers: authHeaders });
      if (r.data.ok) toast.success(`Test sent → ${testTo}`);
      else toast.error(`Send failed: ${r.data.error || "unknown"}`);
    } catch (e) {
      toast.error(`Send failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="email-hub-editor">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5"/>
            <span className="font-mono text-sm">{templateKey}</span>
            {tpl && <BrandBadge brand={tpl.brand}/>}
            {dirty && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">unsaved</Badge>}
          </DialogTitle>
        </DialogHeader>

        {!tpl ? <div className="text-sm text-[#6F8FA8]">Loading…</div> : (
          <div className="space-y-4">
            <div className="text-xs text-[#6F8FA8] bg-[#F5F3EE] rounded-lg p-3">
              <strong>Trigger:</strong> {tpl.trigger || "—"}<br/>
              <strong>Description:</strong> {tpl.description || "—"}<br/>
              <strong className="text-emerald-700">Editable:</strong> subject, preview text, headline, body — the HTML shell (logo, colours, footer) stays locked per brand.
            </div>

            {field("subject", "Subject line", false, 1, "editor-subject")}
            {field("preview_text", "Preview text (inbox snippet)", false, 1, "editor-preview-text")}
            {field("headline", "Headline (H1 in email body)", false, 1, "editor-headline")}
            {field("body", "Body copy — plain text with blank-line paragraphs. Use [Label](https://url) for buttons.", true, 12, "editor-body")}
            {field("description", "Description (admin-only note)", true, 2, "editor-description")}

            <div className="text-xs text-[#6F8FA8]">
              <strong>Available tokens:</strong> <code>{"{{first_name}}"}</code> <code>{"{{last_name}}"}</code> <code>{"{{email}}"}</code> <code>{"{{amount}}"}</code> <code>{"{{receipt_url}}"}</code> <code>{"{{download_url}}"}</code> <code>{"{{portal_url}}"}</code> — any key you set in the trigger's context dict resolves.
            </div>

            <div className="flex items-center gap-2 pt-2 border-t border-[#e2dccd]">
              <Input
                placeholder="you@example.com"
                value={testTo} onChange={e => setTestTo(e.target.value)}
                className="flex-1 max-w-[280px]"
                data-testid="editor-test-to"
              />
              <Button variant="outline" onClick={sendTest} data-testid="editor-send-test">
                <Send className="h-4 w-4 mr-1"/>Send test
              </Button>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="editor-cancel"><X className="h-4 w-4 mr-1"/>Close</Button>
          <Button
            variant="outline"
            onClick={openPreview}
            disabled={!tpl}
            data-testid="editor-preview"
          ><Eye className="h-4 w-4 mr-1"/>Preview branded shell</Button>
          <Button
            className="bg-[#0D2F4D] hover:bg-[#0D2F4D]/90 text-[#C6D21F]"
            onClick={save} disabled={saving || !dirty}
            data-testid="editor-save"
          ><CheckCircle2 className="h-4 w-4 mr-1"/>{saving ? "Saving…" : "Save changes"}</Button>
        </DialogFooter>
      </DialogContent>
      <Dialog open={previewOpen} onOpenChange={(o) => !o && setPreviewOpen(false)}>
        <DialogContent className="max-w-4xl h-[92vh] flex flex-col p-0" data-testid="editor-preview-dialog">
          <DialogHeader className="px-6 py-4 border-b border-[#E4E7EA] shrink-0">
            <DialogTitle className="flex items-center gap-2 text-[#0D2F4D]">
              <Eye className="h-4 w-4"/> Branded shell preview
              {tpl && <BrandBadge brand={tpl.brand}/>}
              <span className="font-mono text-xs text-[#6F8FA8] ml-2">{templateKey}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden bg-[#F2F2EF] px-4 py-4">
            {previewing && <p className="text-sm text-[#6F8FA8] p-6">Rendering…</p>}
            {previewErr && <p className="text-sm text-red-600 p-6">Preview failed: {previewErr}</p>}
            {!previewing && !previewErr && (
              <iframe
                title="template-preview"
                srcDoc={previewHtml}
                data-testid="editor-preview-iframe"
                className="w-full h-full bg-white rounded-lg shadow-sm border border-[#E4E7EA]"
                sandbox=""
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

/* ── Send log ────────────────────────────────────────────────────────── */
function Logs({ authHeaders }) {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [brand, setBrand] = useState("all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    const params = new URLSearchParams();
    params.set("limit", "100");
    if (brand !== "all") params.set("brand", brand);
    if (status !== "all") params.set("status", status);
    if (q.trim()) params.set("q", q.trim());
    axios.get(`${API}/admin/email-hub/logs?${params.toString()}`, { headers: authHeaders })
      .then(r => { setLogs(r.data.logs || []); setTotal(r.data.total || 0); })
      .catch(e => toast.error(`Logs load failed: ${e?.response?.data?.detail || e.message}`));
  }, [authHeaders, brand, status, q]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-center flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6F8FA8]"/>
          <Input placeholder="Search recipient email or subject" className="pl-9" value={q} onChange={e => setQ(e.target.value)} data-testid="email-hub-logs-search"/>
        </div>
        <Select value={brand} onValueChange={setBrand}>
          <SelectTrigger className="w-[160px]" data-testid="email-hub-logs-brand"><SelectValue placeholder="Brand"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All brands</SelectItem>
            <SelectItem value="collective">Collective</SelectItem>
            <SelectItem value="careers">Careers</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[140px]" data-testid="email-hub-logs-status"><SelectValue placeholder="Status"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="skipped">Skipped</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={load} data-testid="email-hub-logs-refresh">
          <RefreshCw className="h-4 w-4 mr-1"/>Refresh
        </Button>
        <div className="text-xs text-[#6F8FA8]">Showing {logs.length} of {total}</div>
      </div>

      <Card>
        <Table data-testid="email-hub-logs-table">
          <TableHeader>
            <TableRow>
              <TableHead>Sent at</TableHead>
              <TableHead>Brand</TableHead>
              <TableHead>Template</TableHead>
              <TableHead>To</TableHead>
              <TableHead>Subject</TableHead>
              <TableHead className="w-[100px]">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.map(l => (
              <TableRow key={l.id} data-testid={`email-hub-log-row-${l.id}`}>
                <TableCell className="text-xs whitespace-nowrap">{new Date(l.sent_at).toLocaleString()}</TableCell>
                <TableCell><BrandBadge brand={l.brand}/></TableCell>
                <TableCell className="font-mono text-xs">{l.template_key}</TableCell>
                <TableCell className="text-xs">{l.to_email}</TableCell>
                <TableCell className="text-xs max-w-[300px] truncate" title={l.subject}>{l.subject}</TableCell>
                <TableCell>
                  <StatusBadge status={l.status}/>
                  {l.opened_at && (
                    <div className="text-[10px] text-emerald-700 mt-1 font-semibold" data-testid={`log-opened-${l.id}`}>
                      · opened {new Date(l.opened_at).toLocaleString()}
                    </div>
                  )}
                  {Array.isArray(l.clicks) && l.clicks.length > 0 && (
                    <div className="text-[10px] text-sky-700 mt-0.5 font-semibold" data-testid={`log-clicks-${l.id}`}>
                      · {l.clicks.length} click{l.clicks.length === 1 ? "" : "s"}
                    </div>
                  )}
                  {l.bounced_at && (
                    <div className="text-[10px] text-red-700 mt-0.5 font-semibold">· bounced</div>
                  )}
                  {l.status === "failed" && l.error && <div className="text-[10px] text-red-700 mt-1 max-w-[220px] truncate" title={l.error}>{l.error}</div>}
                </TableCell>
              </TableRow>
            ))}
            {logs.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-sm text-[#6F8FA8] py-6">No sends match</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

export default EmailHubTab;

/* ─────────────────────────── Marketing subscribers ─────────────────── */
function Subscribers({ authHeaders }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [brand, setBrand] = useState("all");
  const [tagFilter, setTagFilter] = useState("");
  const [tagCatalog, setTagCatalog] = useState([]);
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [backfillPreview, setBackfillPreview] = useState(null);
  const [backfillBrand, setBackfillBrand] = useState("collective");
  const [backfillWelcome, setBackfillWelcome] = useState(false);
  const [backfillTag, setBackfillTag] = useState("");
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [tagEditRow, setTagEditRow] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [newSubscriber, setNewSubscriber] = useState({
    email: "", first_name: "", brand: "careers", tags: "Outreach Target",
  });

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (brand !== "all") p.set("brand", brand);
    p.set("limit", "500");
    axios.get(`${API}/admin/email-hub/subscribers?${p}`, { headers: authHeaders })
      .then(r => { setRows(r.data.subscribers || []); setTotal(r.data.total || 0); })
      .catch(e => toast.error(`Load failed: ${e?.response?.data?.detail || e.message}`));
    axios.get(`${API}/admin/email-hub/tags`, { headers: authHeaders })
      .then(r => setTagCatalog(r.data.tags || []))
      .catch(() => {});
  }, [authHeaders, brand]);
  useEffect(() => { load(); }, [load]);

  const filteredRows = tagFilter
    ? rows.filter(r => (r.tags || []).includes(tagFilter))
    : rows;

  const removeSub = async (id) => {
    if (!window.confirm("Unsubscribe this contact from the marketing list?")) return;
    try {
      await axios.delete(`${API}/admin/email-hub/subscribers/${id}`, { headers: authHeaders });
      toast.success("Unsubscribed");
      load();
    } catch (e) { toast.error(`Failed: ${e?.response?.data?.detail || e.message}`); }
  };

  const saveTags = async (id, tagsStr) => {
    const tags = tagsStr.split(",").map(t => t.trim()).filter(Boolean);
    try {
      await axios.patch(`${API}/admin/email-hub/subscribers/${id}`, { tags }, { headers: authHeaders });
      toast.success("Tags saved");
      setTagEditRow(null);
      load();
    } catch (e) { toast.error(`Failed: ${e?.response?.data?.detail || e.message}`); }
  };

  const addSubscriber = async () => {
    if (!newSubscriber.email.trim()) return toast.error("Email is required.");
    setAddBusy(true);
    try {
      await axios.post(`${API}/admin/email-hub/subscribers`, {
        email: newSubscriber.email.trim(),
        first_name: newSubscriber.first_name.trim(),
        brand: newSubscriber.brand,
        tags: newSubscriber.tags.split(",").map(t => t.trim()).filter(Boolean),
      }, { headers: authHeaders });
      toast.success("Subscriber added to the Marketing List.");
      setAddOpen(false);
      setNewSubscriber({ email: "", first_name: "", brand: "careers", tags: "Outreach Target" });
      load();
    } catch (e) {
      toast.error(`Add failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setAddBusy(false); }
  };

  const runBackfill = async (dry_run) => {
    setBackfillBusy(true);
    try {
      const r = await axios.post(`${API}/admin/email-hub/backfill-subscribers`,
        {
          brand_default: backfillBrand,
          dry_run,
          send_welcome: !dry_run && backfillWelcome,
          welcome_tag: backfillTag.trim() || null,
        },
        { headers: authHeaders });
      setBackfillPreview(r.data);
      if (!dry_run) {
        const bits = [`Added ${r.data.inserted || 0} new`];
        if (r.data.welcome_sent !== undefined) bits.push(`${r.data.welcome_sent} welcome emails sent`);
        toast.success(bits.join(" · "));
        load();
      }
    } catch (e) { toast.error(`Backfill failed: ${e?.response?.data?.detail || e.message}`); }
    finally { setBackfillBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-center flex-wrap">
        <div className="flex items-center gap-2 text-sm text-[#0D2F4D]">
          <Users className="h-4 w-4"/><b>{total}</b> total · <b>{filteredRows.length}</b> shown
        </div>
        <div className="ml-auto flex gap-2 flex-wrap">
          <Button
            size="sm"
            onClick={() => setAddOpen(true)}
            data-testid="subscribers-add-open"
            className="bg-[#0D2F4D] text-[#C6D21F] hover:bg-[#0D2F4D]/90"
          ><Plus className="h-4 w-4 mr-1"/>Add Subscriber</Button>
          <Button
            variant="outline" size="sm"
            onClick={() => { setBackfillOpen(true); setBackfillPreview(null); }}
            data-testid="subs-backfill-open"
            className="border-[#C6D21F] text-[#0D2F4D] hover:bg-[#F7F9E8]"
          ><Plus className="h-4 w-4 mr-1"/>Backfill from CRM</Button>
          <Select value={tagFilter || "__all__"} onValueChange={(v) => setTagFilter(v === "__all__" ? "" : v)}>
            <SelectTrigger className="w-[180px]" data-testid="subs-tag-filter"><SelectValue placeholder="All tags"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All tags</SelectItem>
              {tagCatalog.map(t => (
                <SelectItem key={t.tag} value={t.tag}>{t.tag} · {t.count}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={brand} onValueChange={setBrand}>
            <SelectTrigger className="w-[160px]" data-testid="subs-brand-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All brands</SelectItem>
              <SelectItem value="collective">Collective</SelectItem>
              <SelectItem value="careers">Careers</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load} data-testid="subs-refresh"><RefreshCw className="h-4 w-4 mr-1"/>Refresh</Button>
        </div>
      </div>
      <Card>
        <Table data-testid="subs-table">
          <TableHeader>
            <TableRow>
              <TableHead>Subscribed</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>First name</TableHead>
              <TableHead>Brand</TableHead>
              <TableHead>Tags</TableHead>
              <TableHead>Source</TableHead>
              <TableHead className="w-[70px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRows.map(s => (
              <TableRow key={s.id} data-testid={`subs-row-${s.id}`}>
                <TableCell className="text-xs whitespace-nowrap">{new Date(s.created_at).toLocaleDateString()}</TableCell>
                <TableCell className="text-sm">{s.email}</TableCell>
                <TableCell className="text-sm">{s.first_name || <span className="text-[#94A3B8]">—</span>}</TableCell>
                <TableCell><BrandBadge brand={s.brand}/></TableCell>
                <TableCell className="text-xs">
                  {tagEditRow === s.id ? (
                    <div className="flex gap-1 items-center">
                      <Input
                        autoFocus
                        defaultValue={(s.tags || []).join(", ")}
                        className="h-7 text-xs w-[180px]"
                        data-testid={`subs-tag-input-${s.id}`}
                        onKeyDown={e => {
                          if (e.key === "Enter") saveTags(s.id, e.target.value);
                          if (e.key === "Escape") setTagEditRow(null);
                        }}
                        onBlur={e => saveTags(s.id, e.target.value)}
                        placeholder="tag1, tag2"
                      />
                    </div>
                  ) : (
                    <button
                      className="flex flex-wrap gap-1 items-center min-h-[24px] cursor-text w-full text-left"
                      onClick={() => setTagEditRow(s.id)}
                      data-testid={`subs-tags-cell-${s.id}`}
                      title="Click to edit tags"
                    >
                      {(s.tags || []).map(t => (
                        <span key={t} className="px-2 py-0.5 rounded-full bg-[#F7F9E8] text-[#0D2F4D] border border-[#C6D21F]/40 text-[10px]">{t}</span>
                      ))}
                      {(!s.tags || s.tags.length === 0) && <span className="text-[#94A3B8] text-[10px] italic">+ add tags</span>}
                    </button>
                  )}
                </TableCell>
                <TableCell className="text-xs text-[#6F8FA8]">
                  <EntryTypeBadge type={s.entry_type} id={s.id}/>
                  <div className="mt-1 max-w-[160px] truncate" title={s.source_first}>{s.source_first}</div>
                </TableCell>
                <TableCell><Button size="sm" variant="ghost" onClick={() => removeSub(s.id)} data-testid={`subs-remove-${s.id}`}><Trash2 className="h-4 w-4 text-red-500"/></Button></TableCell>
              </TableRow>
            ))}
            {filteredRows.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-6 text-sm text-[#6F8FA8]">No subscribers match — try a different tag or brand.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg" data-testid="subscriber-add-dialog">
          <DialogHeader><DialogTitle>Add Subscriber</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-[#6F8FA8]">Email</label>
              <Input
                type="email"
                value={newSubscriber.email}
                onChange={e => setNewSubscriber(v => ({ ...v, email: e.target.value }))}
                data-testid="subscriber-add-email"
                placeholder="contact@company.com"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-[#6F8FA8]">First name</label>
              <Input
                value={newSubscriber.first_name}
                onChange={e => setNewSubscriber(v => ({ ...v, first_name: e.target.value }))}
                data-testid="subscriber-add-first-name"
                placeholder="First name"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-[#6F8FA8]">Brand</label>
              <Select value={newSubscriber.brand} onValueChange={brand => setNewSubscriber(v => ({ ...v, brand }))}>
                <SelectTrigger data-testid="subscriber-add-brand"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="careers">The Ditch Careers</SelectItem>
                  <SelectItem value="collective">The Ditch Collective</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-[#6F8FA8]">Tags</label>
              <Input
                value={newSubscriber.tags}
                onChange={e => setNewSubscriber(v => ({ ...v, tags: e.target.value }))}
                data-testid="subscriber-add-tags"
                placeholder="Outreach Target, Employer"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} data-testid="subscriber-add-cancel">Cancel</Button>
            <Button
              onClick={addSubscriber}
              disabled={addBusy}
              data-testid="subscriber-add-submit"
              className="bg-[#0D2F4D] text-[#C6D21F] hover:bg-[#0D2F4D]/90"
            >{addBusy ? "Adding…" : "Add Subscriber"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={backfillOpen} onOpenChange={(o) => !o && setBackfillOpen(false)}>
        <DialogContent className="max-w-2xl" data-testid="subs-backfill-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0D2F4D]">Backfill subscribers from CRM</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <p className="text-[#334155]">
              Sweeps every corner of the CRM — leads, clients, members, buyers,
              candidates, applicants, resume reviews, subscriptions — and adds
              anyone with a real email into the marketing list. This is safe to
              re-run: it never duplicates, never resurrects an unsubscribed
              contact, and never adds test / placeholder addresses.
            </p>
            <div className="grid md:grid-cols-2 gap-4 items-end">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-[#6F8FA8] block mb-1">Default brand for un-tagged rows</label>
                <Select value={backfillBrand} onValueChange={setBackfillBrand}>
                  <SelectTrigger data-testid="subs-backfill-brand"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="collective">Collective</SelectItem>
                    <SelectItem value="careers">Careers</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-[#6F8FA8] block mb-1">Tag every backfilled row (optional)</label>
                <Input value={backfillTag} onChange={e => setBackfillTag(e.target.value)} placeholder="e.g. legacy-import" data-testid="subs-backfill-tag" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={backfillWelcome}
                onChange={e => setBackfillWelcome(e.target.checked)}
                data-testid="subs-backfill-welcome"
                className="accent-[#0D2F4D]"
              />
              <span>Send "welcome back" branded email to each new subscriber</span>
            </label>
            {backfillPreview && (
              <Card className="bg-[#F7F9E8] border-[#C6D21F]/40">
                <CardContent className="p-4 space-y-2 text-xs font-mono text-[#0D2F4D]">
                  <div className="flex gap-6 flex-wrap">
                    <span><b>New to add:</b> {backfillPreview.total_new_to_insert}</span>
                    <span><b>Already in list:</b> {backfillPreview.total_skipped_existing}</span>
                    <span><b>Filtered (test/bad):</b> {backfillPreview.total_skipped_bad}</span>
                    <span><b>List size before:</b> {backfillPreview.existing_list_size}</span>
                    {backfillPreview.new_list_size !== undefined && (
                      <span className="text-[#059669]"><b>List size after:</b> {backfillPreview.new_list_size}</span>
                    )}
                    {backfillPreview.welcome_sent !== undefined && (
                      <span className="text-[#0369A1]"><b>Welcome sent:</b> {backfillPreview.welcome_sent}{backfillPreview.welcome_failed ? ` · ${backfillPreview.welcome_failed} failed` : ""}</span>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2 pt-2 border-t border-[#C6D21F]/40">
                    {Object.entries(backfillPreview.per_source).map(([src, c]) => (
                      <div key={src} className="flex justify-between">
                        <span className="text-[#334155]">{src}</span>
                        <span><b>{c.added}</b> new / {c.scanned} scanned</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBackfillOpen(false)} data-testid="subs-backfill-cancel">Close</Button>
            <Button
              variant="outline"
              onClick={() => runBackfill(true)}
              disabled={backfillBusy}
              data-testid="subs-backfill-dryrun"
            ><Eye className="h-4 w-4 mr-1"/>{backfillBusy ? "Scanning…" : "Preview (dry-run)"}</Button>
            <Button
              onClick={() => runBackfill(false)}
              disabled={backfillBusy || !backfillPreview || backfillPreview.total_new_to_insert === 0}
              className="bg-[#0D2F4D] hover:bg-[#0D2F4D]/90 text-[#C6D21F]"
              data-testid="subs-backfill-commit"
            ><CheckCircle2 className="h-4 w-4 mr-1"/>{backfillBusy ? "Adding…" : `Commit — add ${backfillPreview?.total_new_to_insert ?? 0}`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─────────────────────────────── Campaigns ─────────────────────────── */
function Campaigns({ authHeaders }) {
  const [rows, setRows] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [stats, setStats] = useState({}); // { campaign_id: { open_rate, click_rate, unsub_rate, ... } }

  const load = useCallback(() => {
    axios.get(`${API}/admin/email-hub/campaigns`, { headers: authHeaders })
      .then(async r => {
        const list = r.data.campaigns || [];
        setRows(list);
        // Fire stats fetches for all "sent" campaigns in parallel
        const sentIds = list.filter(c => c.status === "sent").map(c => c.id);
        const results = await Promise.allSettled(
          sentIds.map(id => axios.get(`${API}/admin/email-hub/campaigns/${id}/stats`, { headers: authHeaders }).then(r => [id, r.data]))
        );
        const next = {};
        for (const r of results) {
          if (r.status === "fulfilled") {
            const [id, data] = r.value;
            next[id] = data;
          }
        }
        setStats(next);
      })
      .catch(e => toast.error(`Load failed: ${e?.response?.data?.detail || e.message}`));
  }, [authHeaders]);
  useEffect(() => { load(); }, [load]);

  const sendIt = async (c) => {
    const ok = window.confirm(`Send "${c.name}" to the ${c.audience.replace(/_/g," ")} audience right now?\nThis cannot be undone.`);
    if (!ok) return;
    try {
      const r = await axios.post(`${API}/admin/email-hub/campaigns/${c.id}/send`, {}, { headers: authHeaders });
      toast.success(`Sent ${r.data.sent_count}/${r.data.recipient_count} — ${r.data.failed_count} failed`);
      load();
    } catch (e) { toast.error(`Send failed: ${e?.response?.data?.detail || e.message}`); }
  };

  const preview = async (c) => {
    try {
      const r = await axios.post(`${API}/admin/email-hub/campaigns/${c.id}/preview`, {}, { headers: authHeaders });
      toast.info(`Audience: ${r.data.audience_size} recipients · sample: ${r.data.sample.slice(0,3).join(", ")}${r.data.sample.length>3?"…":""}`);
    } catch (e) { toast.error(`Preview failed: ${e?.response?.data?.detail || e.message}`); }
  };

  const rm = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    try { await axios.delete(`${API}/admin/email-hub/campaigns/${c.id}`, { headers: authHeaders }); toast.success("Deleted"); load(); }
    catch (e) { toast.error(`Delete failed: ${e?.response?.data?.detail || e.message}`); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-bold text-[#0D2F4D] flex items-center gap-2"><Megaphone className="h-5 w-5"/>Marketing campaigns</h3>
          <p className="text-xs text-[#6F8FA8]">Blast a monthly newsletter or one-off announcement to any of your live audiences. All test / example emails are auto-excluded.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              if (!window.confirm("Send this week's digest to every inquirer now?")) return;
              try {
                const r = await axios.post(`${API}/admin/email-hub/send-weekly-digest-now`, {}, { headers: authHeaders });
                toast.success(`Digest fired · ${r.data.sent} sent, ${r.data.skipped} skipped`);
              } catch (e) { toast.error(`Digest failed: ${e?.response?.data?.detail || e.message}`); }
            }}
            data-testid="digest-send-now"
          ><Mail className="h-4 w-4 mr-1"/>Weekly digest now</Button>
          <Button
            variant="outline"
            onClick={async () => {
              try {
                const r = await axios.post(`${API}/admin/automation/sweep-abandoned-now`, {}, { headers: authHeaders });
                toast.success(`Swept · ${r.data.nudges_sent} abandoned-cart nudges sent`);
              } catch (e) { toast.error(`Sweep failed: ${e?.response?.data?.detail || e.message}`); }
            }}
            data-testid="abandoned-sweep-now"
          ><RefreshCw className="h-4 w-4 mr-1"/>Sweep abandoned</Button>
          <Button
            className="bg-[#0D2F4D] hover:bg-[#0D2F4D]/90 text-[#C6D21F]"
            onClick={() => setShowNew(true)} data-testid="campaigns-new"
          ><Plus className="h-4 w-4 mr-1"/>New campaign</Button>
        </div>
      </div>

      <Card>
        <Table data-testid="campaigns-table">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Brand</TableHead>
              <TableHead>Audience</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Sent</TableHead>
              <TableHead>Insights</TableHead>
              <TableHead className="w-[220px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(c => (
              <TableRow key={c.id} data-testid={`campaign-row-${c.id}`}>
                <TableCell className="text-sm font-semibold text-[#0D2F4D]">
                  {c.name}
                  {c.tag && <div className="text-[10px] text-[#0369A1] font-mono mt-0.5">tag: {c.tag}</div>}
                </TableCell>
                <TableCell><BrandBadge brand={c.brand}/></TableCell>
                <TableCell className="text-xs">{c.audience.replace(/_/g," ")}</TableCell>
                <TableCell>
                  {c.status === "draft"   && <Badge className="bg-slate-100 text-slate-800">draft</Badge>}
                  {c.status === "sending" && <Badge className="bg-amber-100 text-amber-800">sending…</Badge>}
                  {c.status === "sent"    && <Badge className="bg-emerald-100 text-emerald-800">sent</Badge>}
                </TableCell>
                <TableCell className="text-xs">{c.sent_count}/{c.recipient_count}{c.failed_count>0 && <span className="text-red-600 ml-1">({c.failed_count} failed)</span>}</TableCell>
                <TableCell className="text-xs" data-testid={`campaign-stats-${c.id}`}>
                  {stats[c.id] ? (
                    <div className="flex gap-3 whitespace-nowrap">
                      <span title="Open rate" className="text-emerald-700"><b>{stats[c.id].open_rate}%</b> open</span>
                      <span title="Click rate" className="text-blue-700"><b>{stats[c.id].click_rate}%</b> click</span>
                      <span title="Unsub rate (attribution: 30d after send)" className={stats[c.id].unsub_rate > 1 ? "text-red-600" : "text-[#6F8FA8]"}><b>{stats[c.id].unsub_rate}%</b> unsub</span>
                    </div>
                  ) : (
                    <span className="text-[#94A3B8]">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" variant="outline" onClick={() => preview(c)} data-testid={`campaign-preview-${c.id}`}><Eye className="h-3.5 w-3.5"/></Button>
                  {c.status !== "sent" && c.status !== "sending" && (
                    <Button size="sm" variant="outline" onClick={() => setEditing(c)} data-testid={`campaign-edit-${c.id}`}><Edit2 className="h-3.5 w-3.5 mr-1"/>Edit</Button>
                  )}
                  {c.status !== "sent" && c.status !== "sending" && (
                    <Button size="sm" className="bg-[#C6D21F] text-[#0D2F4D] hover:bg-[#C6D21F]/90" onClick={() => sendIt(c)} data-testid={`campaign-send-${c.id}`}><Send className="h-3.5 w-3.5 mr-1"/>Send</Button>
                  )}
                  {c.status !== "sending" && (
                    <Button size="sm" variant="ghost" onClick={() => rm(c)} data-testid={`campaign-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5 text-red-500"/></Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (<TableRow><TableCell colSpan={7} className="text-center py-8 text-sm text-[#6F8FA8]">No campaigns yet. Hit "New campaign" to draft your first send.</TableCell></TableRow>)}
          </TableBody>
        </Table>
      </Card>

      {showNew && <CampaignComposer authHeaders={authHeaders} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }}/>}
      {editing && <CampaignComposer authHeaders={authHeaders} editing={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}/>}
    </div>
  );
}

function CampaignComposer({ authHeaders, editing, onClose, onSaved }) {
  const [form, setForm] = useState(() => editing ? {
    name: editing.name || "",
    brand: editing.brand || "collective",
    audience: editing.audience || "marketing_list",
    tag: editing.tag || "",
    subject: editing.subject || "",
    preview_text: editing.preview_text || "",
    headline: editing.headline || "",
    body: editing.body || "",
  } : {
    name: "",
    brand: "collective",
    audience: "marketing_list",
    tag: "",
    subject: "",
    preview_text: "",
    headline: "",
    body: "",
  });
  const [tagCatalog, setTagCatalog] = useState([]);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);
  const set = (patch) => setForm(f => ({ ...f, ...patch }));

  useEffect(() => {
    axios.get(`${API}/admin/email-hub/tags`, { headers: authHeaders })
      .then(r => setTagCatalog(r.data.tags || []))
      .catch(() => {});
  }, [authHeaders]);

  const sendTest = async () => {
    if (!testEmail.trim()) { toast.error("Enter an email to send the test to."); return; }
    if (!form.subject || !form.body) { toast.error("Subject and body are required before testing."); return; }
    setTesting(true);
    try {
      const r = await axios.post(`${API}/admin/email-hub/campaigns/test-send`, {
        to_email: testEmail.trim(),
        brand: form.brand,
        subject: form.subject,
        preview_text: form.preview_text,
        headline: form.headline,
        body: form.body,
      }, { headers: authHeaders });
      if (r.data?.ok) toast.success(`Test sent to ${testEmail.trim()} — check your inbox.`);
      else toast.error(`Test failed: ${r.data?.error || "unknown error"}`);
    } catch (e) { toast.error(`Test failed: ${e?.response?.data?.detail || e.message}`); }
    finally { setTesting(false); }
  };

  const save = async () => {
    if (!form.name || !form.subject || !form.body) { toast.error("Name, subject, and body are required."); return; }
    setSaving(true);
    try {
      if (editing?.id) {
        await axios.patch(`${API}/admin/email-hub/campaigns/${editing.id}`, form, { headers: authHeaders });
        toast.success("Changes saved. Preview → then Send when ready.");
      } else {
        await axios.post(`${API}/admin/email-hub/campaigns`, form, { headers: authHeaders });
        toast.success("Campaign saved as draft. Preview → then Send when ready.");
      }
      onSaved();
    } catch (e) { toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="campaign-composer">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Megaphone className="h-5 w-5"/>{editing ? "Edit campaign" : "New campaign"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-[#6F8FA8] uppercase tracking-wide">Internal name</label>
            <Input value={form.name} onChange={e => set({ name: e.target.value })} placeholder="Feb 2026 — CV Builder launch" data-testid="composer-name"/>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-[#6F8FA8] uppercase tracking-wide">Brand</label>
              <Select value={form.brand} onValueChange={v => set({ brand: v })}>
                <SelectTrigger data-testid="composer-brand"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="collective">Collective</SelectItem>
                  <SelectItem value="careers">Careers</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#6F8FA8] uppercase tracking-wide">Audience</label>
              <Select value={form.audience} onValueChange={v => set({ audience: v })}>
                <SelectTrigger data-testid="composer-audience"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="marketing_list">Newsletter list (popup opt-ins)</SelectItem>
                  <SelectItem value="leads">All leads</SelectItem>
                  <SelectItem value="clients">All clients</SelectItem>
                  <SelectItem value="candidates">All candidates (Careers)</SelectItem>
                  <SelectItem value="all_real">Everyone real (dedup + no test emails)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {tagCatalog.length > 0 && (
            <div>
              <label className="text-xs font-semibold text-[#6F8FA8] uppercase tracking-wide">Segment tag filter (optional)</label>
              <Select value={form.tag || "__none__"} onValueChange={v => set({ tag: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="composer-tag"><SelectValue placeholder="No tag filter (send to whole audience)"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No tag filter — send to whole audience</SelectItem>
                  {tagCatalog.map(t => (
                    <SelectItem key={t.tag} value={t.tag}>{t.tag} — {t.count} subscriber{t.count === 1 ? "" : "s"}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-[#6F8FA8] mt-1">When set, only marketing-list subscribers carrying this tag will receive the campaign.</p>
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-[#6F8FA8] uppercase tracking-wide">Subject</label>
            <Input value={form.subject} onChange={e => set({ subject: e.target.value })} placeholder="Feb from across the ditch" data-testid="composer-subject"/>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#6F8FA8] uppercase tracking-wide">Preview text (inbox snippet)</label>
            <Input value={form.preview_text} onChange={e => set({ preview_text: e.target.value })} placeholder="What's new this month + 3 things worth reading." data-testid="composer-preview"/>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#6F8FA8] uppercase tracking-wide">Headline (H1)</label>
            <Input value={form.headline} onChange={e => set({ headline: e.target.value })} placeholder="Hi {{first_name}}, welcome back to the ditch." data-testid="composer-headline"/>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#6F8FA8] uppercase tracking-wide">Body — plain text, blank lines separate paragraphs. Use [Label](https://url) for buttons.</label>
            <Textarea rows={12} value={form.body} onChange={e => set({ body: e.target.value })} data-testid="composer-body" className="font-mono text-sm" placeholder={`Hi {{first_name}},\n\nHere's what's new this month...\n\n[Read the update](https://theditchcollective.co.nz)`}/>
          </div>
          <div className="text-xs text-[#6F8FA8] bg-[#F5F3EE] rounded-lg p-3">
            <strong>Tokens available:</strong> <code>{"{{first_name}}"}</code> <code>{"{{email}}"}</code> <code>{"{{brand_name}}"}</code> <code>{"{{site_url}}"}</code>. Test / example / resend.dev addresses are excluded automatically.
          </div>
        </div>
        <div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg bg-[#F5F3EE] p-3" data-testid="composer-test-row">
          <span className="text-xs font-semibold text-[#6F8FA8] uppercase tracking-wide whitespace-nowrap">Send test to</span>
          <Input
            type="email"
            value={testEmail}
            onChange={e => setTestEmail(e.target.value)}
            placeholder="you@theditchcollective.co.nz"
            data-testid="composer-test-email"
            className="flex-1 bg-white"
          />
          <Button variant="outline" onClick={sendTest} disabled={testing} data-testid="composer-send-test" className="whitespace-nowrap border-[#0D2F4D] text-[#0D2F4D]">
            <Send className="h-4 w-4 mr-1"/>{testing ? "Sending…" : "Send test"}
          </Button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="composer-cancel"><X className="h-4 w-4 mr-1"/>Cancel</Button>
          <Button className="bg-[#0D2F4D] hover:bg-[#0D2F4D]/90 text-[#C6D21F]" onClick={save} disabled={saving} data-testid="composer-save">
            <CheckCircle2 className="h-4 w-4 mr-1"/>{saving ? "Saving…" : (editing ? "Save changes" : "Save draft")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



/* ── Stats — Open/Click roll-up ─────────────────────────────────────── */
function Stats({ authHeaders }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/email-hub/stats?days=${days}`, { headers: authHeaders });
      setData(r.data);
    } catch (e) {
      toast.error(`Stats load failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  }, [authHeaders, days]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-sm text-[#6F8FA8]">Loading stats…</div>;

  const t = data.totals;
  const maxSend = Math.max(1, ...data.daily.map(d => d.sent));

  return (
    <div className="space-y-6" data-testid="email-hub-stats">
      <div className="flex items-center gap-3">
        <div className="text-xs uppercase tracking-widest font-bold text-[#6F8FA8]">Window</div>
        {[7, 30, 90].map(d => (
          <button
            key={d}
            onClick={() => setDays(d)}
            data-testid={`stats-window-${d}`}
            className={`text-xs font-bold rounded-full px-3 py-1 border ${days === d ? "bg-[#0D2F4D] text-[#C6D21F] border-[#0D2F4D]" : "bg-white text-[#0D2F4D] border-[#D6DCE3] hover:border-[#0D2F4D]"}`}
          >Last {d}d</button>
        ))}
        <button onClick={load} disabled={loading} className="ml-auto text-xs font-bold text-[#0D2F4D] hover:text-[#C6D21F] disabled:opacity-40">
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {/* Top-level metrics */}
      <div className="grid gap-3 md:grid-cols-4 sm:grid-cols-2">
        {[
          { label: "Sent", value: t.sent, rate: null, color: "text-[#0D2F4D]" },
          { label: "Delivered", value: t.delivered, rate: `${t.delivery_rate}%`, color: "text-emerald-700" },
          { label: "Opened", value: t.opened, rate: `${t.open_rate}%`, color: "text-blue-700" },
          { label: "Clicked", value: t.clicked, rate: `${t.click_rate}%`, color: "text-indigo-700" },
          { label: "Bounced", value: t.bounced, rate: `${t.bounce_rate}%`, color: "text-red-700" },
          { label: "Failed", value: t.failed, rate: null, color: "text-red-700" },
          { label: "Skipped", value: t.skipped, rate: null, color: "text-amber-700" },
        ].map((c) => (
          <Card key={c.label} data-testid={`stats-card-${c.label.toLowerCase()}`}>
            <CardContent className="pt-6 pb-4">
              <div className="text-xs uppercase tracking-wide text-[#6F8FA8] font-bold">{c.label}</div>
              <div className={`text-3xl font-bold mt-1 ${c.color}`}>{c.value}</div>
              {c.rate !== null && <div className="text-xs text-[#6F8FA8] mt-1">{c.rate}</div>}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Daily send curve */}
      {data.daily.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Daily activity</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-end gap-1 h-32">
              {data.daily.map(d => {
                const h = Math.max(4, Math.round((d.sent / maxSend) * 120));
                return (
                  <div key={d.date} className="flex-1 flex flex-col items-center gap-1 min-w-0" title={`${d.date}: ${d.sent} sent · ${d.opened} opened · ${d.clicked} clicked`}>
                    <div className="w-full bg-[#C6D21F] rounded-t" style={{ height: h }}/>
                    <div className="text-[9px] text-[#6F8FA8] truncate w-full text-center">{d.date.slice(5)}</div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Per-template breakdown */}
      <Card>
        <CardHeader><CardTitle className="text-base">Templates & campaigns · last {data.window_days} days</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table data-testid="stats-per-template">
            <TableHeader>
              <TableRow>
                <TableHead>Template</TableHead>
                <TableHead>Brand</TableHead>
                <TableHead className="text-right">Sent</TableHead>
                <TableHead className="text-right">Delivered</TableHead>
                <TableHead className="text-right">Opened</TableHead>
                <TableHead className="text-right">Clicked</TableHead>
                <TableHead className="text-right">Bounced</TableHead>
                <TableHead className="text-right">Open %</TableHead>
                <TableHead className="text-right">Click %</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.per_template.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-[#6F8FA8]">No sends in this window yet.</TableCell></TableRow>
              )}
              {data.per_template.map((r, i) => (
                <TableRow key={`${r.template_key}-${r.brand}-${i}`}>
                  <TableCell><code className="text-xs text-[#0D2F4D]">{r.template_key}</code></TableCell>
                  <TableCell><BrandBadge brand={r.brand}/></TableCell>
                  <TableCell className="text-right font-semibold">{r.sent}</TableCell>
                  <TableCell className="text-right text-emerald-700">{r.delivered}</TableCell>
                  <TableCell className="text-right text-blue-700">{r.opened}</TableCell>
                  <TableCell className="text-right text-indigo-700">{r.clicked}</TableCell>
                  <TableCell className="text-right text-red-700">{r.bounced}</TableCell>
                  <TableCell className="text-right font-semibold">{r.open_rate}%</TableCell>
                  <TableCell className="text-right font-semibold">{r.click_rate}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <p className="text-xs text-[#6F8FA8]">
        Open + click tracking requires Resend event webhook to be pointed at{" "}
        <code>/api/webhooks/resend</code>. Set <code>RESEND_WEBHOOK_SECRET</code> to enforce Svix signature verification.
      </p>
    </div>
  );
}


/* ── Segments — named audience filters ──────────────────────────────── */
const SEGMENT_SOURCES = [
  { value: "marketing_list", label: "Marketing list (newsletter subs)" },
  { value: "leads", label: "Leads (intake form)" },
  { value: "clients", label: "Clients (paid packages)" },
  { value: "candidates", label: "Careers candidates" },
  { value: "members", label: "Members (paid subscriptions)" },
  { value: "people", label: "All people (unified CRM)" },
];

function Segments({ authHeaders }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // segment object being edited or {new:true}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/email-hub/segments`, { headers: authHeaders });
      setRows(r.data.segments || []);
    } catch (e) {
      toast.error(`Segments load failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const delSeg = async (s) => {
    if (!window.confirm(`Delete segment "${s.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/email-hub/segments/${s.id}`, { headers: authHeaders });
      toast.success("Segment deleted.");
      load();
    } catch (e) {
      toast.error(`Delete failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  return (
    <div className="space-y-4" data-testid="email-hub-segments">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-[#0D2F4D]">Contact segments</h3>
          <p className="text-sm text-[#6F8FA8]">Named audience filters — reuse them in the campaign composer.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              try {
                const r = await axios.post(`${API}/admin/email-hub/segments/seed-defaults`, {},
                  { headers: authHeaders });
                const d = r?.data || {};
                toast.success(`Defaults ready — ${d.created || 0} created, ${d.skipped || 0} already existed.`);
                load();
              } catch (e) {
                toast.error(`Setup failed: ${e?.response?.data?.detail || e.message}`);
              }
            }}
            data-testid="segments-seed-defaults"
            title="One-click setup: Employers, Active Employers, Outreach Targets, Candidates, Relocation Leads, All Subscribers"
            className="border-[#0D2F4D] text-[#0D2F4D] hover:bg-[#0D2F4D] hover:text-[#C6D21F]"
          >
            <Zap className="h-4 w-4 mr-1"/>Set up newsletter segments
          </Button>
          <Button
            onClick={() => setEditing({ new: true, name: "", description: "", source: "marketing_list", filters: [] })}
            data-testid="segments-new"
            className="bg-[#0D2F4D] hover:bg-[#0D2F4D]/90 text-[#C6D21F]"
          >
            <Plus className="h-4 w-4 mr-1"/>New segment
          </Button>
        </div>
      </div>

      <Card>
        <Table data-testid="segments-table">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Filters</TableHead>
              <TableHead className="text-right">Members</TableHead>
              <TableHead className="w-[120px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={5} className="text-center py-8 text-[#6F8FA8]">Loading…</TableCell></TableRow>}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-[#6F8FA8]">
                No segments yet — click <strong>New segment</strong> to slice your list.
              </TableCell></TableRow>
            )}
            {rows.map(s => (
              <TableRow key={s.id} data-testid={`segment-row-${s.id}`}>
                <TableCell>
                  <div className="font-semibold text-[#0D2F4D]">{s.name}</div>
                  {s.description && <div className="text-xs text-[#6F8FA8]">{s.description}</div>}
                </TableCell>
                <TableCell><Badge variant="outline">{s.source}</Badge></TableCell>
                <TableCell className="text-xs text-[#6F8FA8]">
                  {(s.filters || []).length === 0 ? "All rows" : (s.filters || []).map(f => `${f.field}${f.op !== "eq" ? " "+f.op : ""}: ${Array.isArray(f.value) ? f.value.join(",") : f.value}`).join(" · ")}
                </TableCell>
                <TableCell className="text-right font-bold text-[#0D2F4D]">{s.count}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="outline" onClick={() => setEditing(s)} data-testid={`segment-edit-${s.id}`}>Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => delSeg(s)} data-testid={`segment-delete-${s.id}`} className="text-red-700 ml-1">Delete</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {editing && (
        <SegmentEditor
          initial={editing}
          authHeaders={authHeaders}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function SegmentEditor({ initial, authHeaders, onClose, onSaved }) {
  const [name, setName] = useState(initial.name || "");
  const [description, setDescription] = useState(initial.description || "");
  const [source, setSource] = useState(initial.source || "marketing_list");
  const [filters, setFilters] = useState(initial.filters || []);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const addFilter = () => setFilters([...filters, { field: "tag", op: "eq", value: "" }]);
  const updateFilter = (i, patch) => setFilters(filters.map((f, idx) => idx === i ? { ...f, ...patch } : f));
  const removeFilter = (i) => setFilters(filters.filter((_, idx) => idx !== i));

  const doPreview = async () => {
    // Save temporarily then preview
    try {
      const payload = { name: name || "Preview", description, source, filters };
      let sid = initial.id;
      if (initial.new) {
        const r = await axios.post(`${API}/admin/email-hub/segments`, payload, { headers: authHeaders });
        sid = r.data.segment.id;
        // Immediately delete since this is just a preview
        setPreview(r.data.segment);
        await axios.delete(`${API}/admin/email-hub/segments/${sid}`, { headers: authHeaders });
      } else {
        const r = await axios.patch(`${API}/admin/email-hub/segments/${sid}`, payload, { headers: authHeaders });
        setPreview(r.data.segment);
      }
    } catch (e) {
      toast.error(`Preview failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const save = async () => {
    if (!name.trim()) return toast.error("Segment name required.");
    setSaving(true);
    try {
      const payload = { name: name.trim(), description: description.trim(), source, filters };
      if (initial.new) {
        await axios.post(`${API}/admin/email-hub/segments`, payload, { headers: authHeaders });
        toast.success("Segment created.");
      } else {
        await axios.patch(`${API}/admin/email-hub/segments/${initial.id}`, payload, { headers: authHeaders });
        toast.success("Segment updated.");
      }
      onSaved?.();
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="segment-editor">
        <DialogHeader>
          <DialogTitle>{initial.new ? "New segment" : `Edit — ${initial.name}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-[#6F8FA8]">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="segment-name" placeholder="e.g. Melbourne movers"/>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-[#6F8FA8]">Description (optional)</label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} data-testid="segment-description"/>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-[#6F8FA8]">Source</label>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger data-testid="segment-source"><SelectValue/></SelectTrigger>
              <SelectContent>
                {SEGMENT_SOURCES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs uppercase tracking-widest font-bold text-[#6F8FA8]">Filters (all must match)</label>
              <Button size="sm" variant="outline" onClick={addFilter} data-testid="segment-add-filter">+ Add filter</Button>
            </div>
            {filters.length === 0 && <p className="text-sm text-[#6F8FA8]">No filters — the segment includes every row in the source.</p>}
            <div className="space-y-2">
              {filters.map((f, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Select value={f.field} onValueChange={(v) => updateFilter(i, { field: v })}>
                    <SelectTrigger className="w-[180px]" data-testid={`segment-filter-field-${i}`}><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tag">Tag</SelectItem>
                      <SelectItem value="excludes_tag">Excludes tag</SelectItem>
                      <SelectItem value="source">Source</SelectItem>
                      <SelectItem value="plan">Plan</SelectItem>
                      <SelectItem value="brand">Brand</SelectItem>
                      <SelectItem value="location">Location (contains)</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    value={f.value || ""}
                    onChange={(e) => updateFilter(i, { value: e.target.value })}
                    placeholder="value"
                    className="flex-1"
                    data-testid={`segment-filter-value-${i}`}
                  />
                  <Button size="sm" variant="ghost" onClick={() => removeFilter(i)} data-testid={`segment-filter-remove-${i}`}><X className="h-4 w-4"/></Button>
                </div>
              ))}
            </div>
          </div>

          {preview && (
            <div className="bg-[#F5F3EE] rounded-xl p-4">
              <p className="text-sm font-semibold text-[#0D2F4D] mb-1">Preview: {preview.count} matching contacts</p>
              <p className="text-xs text-[#6F8FA8]">This is a dry-run — sample of matches is available in the API preview.</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={doPreview} data-testid="segment-preview">Preview count</Button>
          <Button variant="outline" onClick={onClose} data-testid="segment-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#0D2F4D] hover:bg-[#0D2F4D]/90 text-[#C6D21F]" data-testid="segment-save">
            {saving ? "Saving…" : (initial.new ? "Create segment" : "Save changes")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
